import React, { useMemo } from "react";
import { DollarSign, Clock, Activity } from "lucide-react";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";

interface JobOverview {
  totalCost?: number | string;
  efforts?: string;
}

interface ServiceOrderData {
  status?: string;
}

interface KPIMetric {
  title: string;
  count: string;
  trendSummary: string;
  icon: React.ReactNode;
  tooltip?: string;
}

// Configuration constants
const CONFIG = {
  defaultValues: {
    cost: "0",
    text: "N/A",
  },
};

// Utility function
const formatCurrency = (
  value: number | string | undefined,
  currency = "$"
): string => {
  const amount = value || CONFIG.defaultValues.cost;
  return `${currency}${amount}`;
};

// KPI calculation logic
export const calculateCompletedKPIData = (
  jobOverview?: JobOverview,
  serviceOrderData?: ServiceOrderData
) => {
  return {
    totalCost: formatCurrency(jobOverview?.totalCost),
    totalTime: jobOverview?.efforts || CONFIG.defaultValues.text,
    status: serviceOrderData?.status || "Completed",
  };
};

// KPI configuration
export const createCompletedKPIMetrics = (kpiData: {
  totalCost: string;
  totalTime: string;
  status: string;
}): KPIMetric[] => [
  {
    title: "Total Cost",
    count: kpiData.totalCost,
    trendSummary: "Actual cost for completed work",
    icon: <DollarSign className="h-8 w-8 text-green-500" />,
    tooltip: "Total cost incurred for the completed service order",
  },
  {
    title: "Total Time",
    count: kpiData.totalTime,
    trendSummary: "Time spent on work completion",
    icon: <Clock className="h-8 w-8 text-blue-500" />,
    tooltip: "Total time taken to complete all tasks",
  },
  {
    title: "Status",
    count: kpiData.status,
    trendSummary: "Service order status",
    icon: <Activity className="h-8 w-8 text-orange-500" />,
    tooltip: "Current status of the service order",
  },
];

interface CompletedServiceOrderKPIProps {
  jobOverview?: JobOverview;
  serviceOrderData?: ServiceOrderData;
  className?: string;
}

// Reusable KPI Dashboard Component
export const CompletedServiceOrderKPI: React.FC<
  CompletedServiceOrderKPIProps
> = ({ jobOverview, serviceOrderData, className = "" }) => {
  const kpiData = useMemo(
    () => calculateCompletedKPIData(jobOverview, serviceOrderData),
    [jobOverview, serviceOrderData]
  );

  const kpiMetrics = useMemo(
    () => createCompletedKPIMetrics(kpiData),
    [kpiData]
  );

  return (
    <div className={`grid gap-6 md:grid-cols-3 ${className}`}>
      {kpiMetrics.map((metric, index) => (
        <QuickStatsCard
          key={index}
          title={metric.title}
          count={metric.count}
          trendSummary={metric.trendSummary}
          icon={metric.icon}
          tooltip={metric.tooltip}
        />
      ))}
    </div>
  );
};

export default CompletedServiceOrderKPI;
